import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  Session,
  SessionMember,
  SessionUserNote,
  ReportType,
  SourceSession,
  GenerateImpairmentRatingResponse,
  MmiForceDecisionParams,
  MmiGateStateResponse,
  RegenerateQmeReportOptions,
  ReportTriggerResponse,
} from '../interfaces/session.interface';
import { environment } from '../../environments/environment';
import { Document } from '../interfaces/document.interface';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { UtilsService } from './utils/utils.service';
import { FileAssetsResponse } from './aiScribe.service';
import { Output } from '../interfaces/task.interface';
import { UserStore } from '../app-state/user.store';
import { GenerationMode } from '../utils/ptp-report.constants';
import { ReportVariant } from '../interfaces/report.interface';

export interface HydratedOutput extends Output {
  value: Record<string, any>;
}

export type SessionListInclude = string | object;

export interface SessionsPageMeta {
  total: number;
  skip: number;
  take: number;
  pageIndex: number;
  pageCount: number;
}

export interface PhysicianOption {
  label: string;
  value: string;
}

export interface SessionsPageResponse {
  data: SourceSession[];
  meta: SessionsPageMeta;
  physicianOptions: PhysicianOption[];
}

export interface GetSessionsPageParams {
  skip?: number;
  take: number;
  search?: string;
  reportType?: string;
  physicianUserId?: string;
  sortBy?: 'createdAt';
  sortOrder?: 'asc' | 'desc';
  includes?: SessionListInclude;
}

function buildPhysicianOptionsFromSessions(
  sessions: SourceSession[],
): PhysicianOption[] {
  const seen = new Set<string>();
  const options: PhysicianOption[] = [];
  for (const session of sessions) {
    const assignee = (session.SessionMember ?? []).find(
      (member: any) => member.role === 'ASSIGNEE',
    );
    const user = assignee?.user;
    if (!user?.id || seen.has(user.id)) continue;
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (!name) continue;
    seen.add(user.id);
    options.push({ label: name, value: user.id });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

function filterLegacySessions(
  sessions: SourceSession[],
  params: GetSessionsPageParams,
): SourceSession[] {
  const search = params.search?.trim().toLowerCase();
  return sessions.filter((session) => {
    if (search) {
      const name = session.value?.toLowerCase() ?? '';
      const claimant =
        `${session.claimant?.firstName ?? ''} ${session.claimant?.lastName ?? ''}`.toLowerCase();
      if (!name.includes(search) && !claimant.includes(search)) {
        return false;
      }
    }
    if (params.reportType && (session.reportType ?? '') !== params.reportType) {
      return false;
    }
    if (params.physicianUserId) {
      const assignee = (session.SessionMember ?? []).find(
        (member: any) => member.role === 'ASSIGNEE',
      );
      if (
        assignee?.userId !== params.physicianUserId &&
        assignee?.user?.id !== params.physicianUserId
      ) {
        return false;
      }
    }
    return true;
  });
}

/** Supports new paginated API and legacy full-array responses. */
function normalizeSessionsPageResponse(
  raw: SessionsPageResponse | SourceSession[],
  params: GetSessionsPageParams,
): SessionsPageResponse {
  if (!Array.isArray(raw)) {
    return raw;
  }

  const filtered = filterLegacySessions(raw, params);
  const sortOrder = params.sortOrder ?? 'desc';
  const sorted = [...filtered].sort((a, b) => {
    const left = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const right = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    const byCreated = sortOrder === 'asc' ? left - right : right - left;
    if (byCreated !== 0) return byCreated;
    return sortOrder === 'asc'
      ? a.id.localeCompare(b.id)
      : b.id.localeCompare(a.id);
  });
  const take = params.take;
  const skip = Math.max(0, params.skip ?? 0);
  const total = sorted.length;
  const pageSize = take;
  const pageIndex = Math.floor(skip / pageSize) + 1;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return {
    data: sorted.slice(skip, skip + take),
    meta: {
      total,
      skip,
      take: pageSize,
      pageIndex,
      pageCount,
    },
    physicianOptions: buildPhysicianOptionsFromSessions(raw),
  };
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly userStore = inject(UserStore);
  constructor(
    private readonly http: HttpClient,
    private readonly utilsService: UtilsService,
  ) {}

  private getOrgId(): string {
    const orgId = this.userStore.organization()?.id;

    if (!orgId) {
      throw new Error('Organization ID not available');
    }

    return orgId;
  }

  getSessions(includes?: SessionListInclude) {
    const includeParam = includes
      ? typeof includes === 'object'
        ? encodeURIComponent(JSON.stringify(includes))
        : includes
      : '';
    return this.http.get<SourceSession[]>(
      environment.api_host + `sessions?include=${includeParam}`,
    );
  }

  getSessionsPage(params: GetSessionsPageParams) {
    const query = new URLSearchParams();
    query.set('take', String(params.take));
    query.set('skip', String(params.skip ?? 0));

    if (params.search?.trim()) {
      query.set('search', params.search.trim());
    }
    if (params.reportType) {
      query.set('reportType', params.reportType);
    }
    if (params.physicianUserId) {
      query.set('physicianUserId', params.physicianUserId);
    }
    if (params.sortBy) {
      query.set('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
      query.set('sortOrder', params.sortOrder);
    }
    if (params.includes) {
      const includeValue =
        typeof params.includes === 'object'
          ? JSON.stringify(params.includes)
          : params.includes;
      query.set('include', includeValue);
    }

    return this.http
      .get<
        SessionsPageResponse | SourceSession[]
      >(`${environment.api_host}sessions?${query.toString()}`)
      .pipe(map((raw) => normalizeSessionsPageResponse(raw, params)));
  }

  getSession(session_id: string, includes?: string | object) {
    const includeParam = includes
      ? typeof includes === 'object'
        ? encodeURIComponent(JSON.stringify(includes))
        : includes
      : '';
    return this.http.get<Session>(
      environment.api_host + `sessions/${session_id}?include=${includeParam}`,
    );
  }

  getReportNotificationPreferences(sessionId: string) {
    return this.http.get<{
      preferences: Array<{ outputType: string; enabled: boolean }>;
    }>(
      environment.api_host +
        `sessions/${sessionId}/report-notification-preferences`,
    );
  }

  setReportNotificationPreference(
    sessionId: string,
    output_type: string,
    enabled: boolean,
  ) {
    return this.http.post<{
      preference: { outputType: string; enabled: boolean };
    }>(
      environment.api_host +
        `sessions/${sessionId}/report-notification-preferences`,
      { output_type, enabled },
    );
  }

  createSession(
    name = 'New Session',
    specialty?: string,
    reportType?: ReportType,
    selectionId?: string,
  ) {
    return this.http.post<SourceSession>(environment.api_host + 'sessions', {
      value: name,
      specialty: specialty,
      reportType: reportType ?? 'qme_report',
      ...(selectionId ? { selection: { id: selectionId } } : {}),
    });
  }

  updateSession(session_id: string, sessionPatch: Partial<Session>) {
    return this.http.patch<Session>(
      environment.api_host + `sessions/${session_id}`,
      sessionPatch,
    );
  }

  getSessionMembers(session_id: string, includes?: string | object) {
    const includeParam = includes
      ? typeof includes === 'object'
        ? encodeURIComponent(JSON.stringify(includes))
        : includes
      : '';
    const url = includeParam
      ? environment.api_host +
        `sessions/${session_id}/members?include=${includeParam}`
      : environment.api_host + `sessions/${session_id}/members`;
    return this.http.get<SessionMember[]>(url);
  }

  getSessionMemberByUserId(session_id: string) {
    return this.http.get<SessionMember>(
      environment.api_host + `sessions/${session_id}/member`,
    );
  }

  addSessionMember(session_id: string, userId: string, role: string) {
    return this.http.post<SessionMember>(
      environment.api_host + `sessions/${session_id}/member`,
      { userId, role },
    );
  }

  updateSessionMember(session_id: string, member_id: string, role: string) {
    return this.http.patch<SessionMember>(
      environment.api_host + `sessions/${session_id}/members/${member_id}`,
      { role },
    );
  }

  removeSessionMember(session_id: string, member_id: string) {
    return this.http.delete(
      environment.api_host + `sessions/${session_id}/members/${member_id}`,
    );
  }

  createFileAsset(
    sessionId: string,
    bucketName: string,
    object_key: string,
    file_extension: string,
    uploaded_by: string,
    hash: string | null,
  ): Observable<FileAssetsResponse> {
    //console.log('Creating file asset with hash:', hash);

    return this.http.post<FileAssetsResponse>(
      environment.api_host + `sessions/${sessionId}/file-assets/create`,
      {
        bucketName: bucketName,
        objectKey: object_key,
        fileExtension: file_extension,
        uploadedBy: uploaded_by,
        hashValue: hash,
      },
    );
  }

  uploadDocumentsToANewSession(
    connection_id: string,
    formData: FormData,
    sessionName: string,
    case_session_id?: string,
  ) {
    formData.append('value', sessionName);

    const uploadRequest = (sessionId: string) =>
      this.http.post<{
        caseSessionId: string;
        session: Session;
        documents: Document[];
      }>(
        environment.api_host +
          `sessions/${sessionId}/documents/upload?connection_id=${connection_id}&case_session_id=${sessionId}`,
        formData,
      );

    if (case_session_id) {
      return uploadRequest(case_session_id);
    }

    return this.createSession(sessionName).pipe(
      switchMap((session) => uploadRequest(session.id)),
    );
  }

  //concurrency limiter
  async runWithConcurrencyLimit<T>(
    tasks: (() => Promise<T>)[],
    limit: number,
  ): Promise<T[]> {
    const results: T[] = [];
    let index = 0;

    async function worker() {
      while (index < tasks.length) {
        const currentIndex = index++;
        results[currentIndex] = await tasks[currentIndex]();
      }
    }

    const workers = Array.from({ length: limit }, () => worker());
    await Promise.all(workers);

    return results;
  }

  async handleMultipartUpload(
    file: NzUploadFile,
    sessionId: string,
    batchId?: string,
    hash: string | null = null,
    onProgress?: (percent: number) => void,
  ): Promise<{
    file: NzUploadFile;
    document: Document | null;
    success: boolean;
  }> {
    try {
      if (!file?.size || !file?.name || !file?.type) {
        console.error('[upload] Invalid file metadata', {
          size: file?.size,
          name: file?.name,
          type: file?.type,
        });
        return { file, document: null, success: false };
      }
      const fileSize = file.size as number;
      const realFile = file.originFileObj ?? (file as unknown as File);

      const chunkSize = 5 * 1024 * 1024; // 5MB
      const totalChunks = Math.ceil(file.size / chunkSize);
      let completedParts = 0;

      let uploadId: string;

      const initResponse = await firstValueFrom(
        this.initiateMultipartUpload(file.name, file.type, sessionId),
      );
      if (!initResponse?.UploadId) {
        throw new Error(
          'Failed to initiate multipart upload — no UploadId returned',
        );
      }
      uploadId = initResponse.UploadId;
      const key = initResponse.Key;
      const tasks: (() => Promise<{ ETag: string; PartNumber: number }>)[] = [];

      for (let i = 0; i < totalChunks; i++) {
        const partNumber = i + 1;

        tasks.push(async () => {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, fileSize);
          const chunk = realFile.slice(start, end);

          const presigned = await firstValueFrom(
            this.getPresignedPartUrl(sessionId, key, uploadId, partNumber),
          );

          const response = await fetch(presigned.url, {
            method: 'PUT',
            body: chunk,
          });

          if (!response.ok) {
            throw new Error(
              `S3 PUT failed for part ${partNumber}: HTTP ${response.status}`,
            );
          }

          const etag = response.headers.get('ETag');
          if (!etag) {
            throw new Error(
              `Missing ETag for part ${partNumber} — ensure S3 CORS config has ExposeHeaders: ["ETag"]`,
            );
          }

          // Report progress after each chunk completes
          completedParts++;
          if (onProgress) {
            // Reserve last 5% for the complete/finalize steps
            const percent = Math.round((completedParts / totalChunks) * 95);
            onProgress(percent);
          }

          return {
            ETag: etag.replace(/"/g, ''),
            PartNumber: partNumber,
          };
        });
      }

      // LIMIT CONCURRENCY HERE
      const parts = await this.runWithConcurrencyLimit(tasks, 5);

      // Ensure parts are sorted by PartNumber
      parts.sort((a, b) => a.PartNumber - b.PartNumber);

      // Complete the multipart upload
      const document = await firstValueFrom(
        this.completeMultipartUpload(
          initResponse.Key,
          uploadId,
          parts,
          sessionId,
          batchId,
        ),
      );

      try {
        await firstValueFrom(
          this.createFileAsset(
            sessionId,
            initResponse.Bucket,
            initResponse.Key,
            file.type,
            '',
            hash || '',
          ),
        );
      } catch (assetErr) {
        console.warn('[upload] createFileAsset failed (non-fatal):', assetErr);
      }

      return { document: document.document, file, success: true };
    } catch (error: any) {
      console.error(
        '[upload] FAILED for file:',
        file.name,
        '| error:',
        error?.message ?? error,
      );
      return {
        document: null,
        file,
        success: false,
      };
    }
  }

  getPresignedPartUrl(
    sessionId: string,
    key: string,
    uploadId: string,
    partNumber: number,
  ) {
    return this.http.post<{ url: string }>(
      environment.api_host +
        `sessions/${sessionId}/documents/presigned-part-url`,
      {
        key,
        uploadId,
        partNumber,
      },
    );
  }

  initiateMultipartUpload(
    fileName: string,
    fileType: string,
    sessionId: string,
  ) {
    const sanitizedFileName = this.utilsService.sanitizeFileName(fileName);
    return this.http.post<any>(
      environment.api_host +
        `sessions/${sessionId}/documents/initiate-multipart-upload`,
      {
        fileName: sanitizedFileName,
        fileType,
      },
    );
  }

  uploadPart(
    sessionId: string,
    uploadId: string,
    partNumber: number,
    chunk: Blob,
    fileName: string,
  ) {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('key', fileName);
    formData.append('uploadId', uploadId);
    formData.append('partNumber', partNumber.toString());

    return this.http.post<any>(
      environment.api_host + `sessions/${sessionId}/documents/upload-part`,
      formData,
    );
  }

  completeMultipartUpload(
    key: string,
    uploadId: string,
    parts: { ETag: string; PartNumber: number }[],
    sessionId: string,
    batchId?: string,
  ) {
    const sanitizedKey = this.utilsService.sanitizeFileName(key);
    // Extract filename from key for title, fallback to 'Document' if not found
    const documentTitle = sanitizedKey.split('/').pop() || 'Document';

    const body: any = {
      key: sanitizedKey,
      uploadId,
      parts,
      language_code: 'en', // Default language code as required by API
      title: documentTitle, // Extract filename as title as required by API
    };

    // Add batch_id if provided
    if (batchId) {
      body.batch_id = batchId;
    }

    return this.http.post<{ document: Document }>(
      environment.api_host +
        `sessions/${sessionId}/documents/complete-multipart-upload`,
      body,
    );
  }

  getSignedUrl(path: string, sessionId: string) {
    return this.http.post<{ signed_url: string }>(
      environment.api_host + `sessions/${sessionId}/documents/signed-url`,
      {
        path,
      },
    );
  }

  documentSearch2(query: string, session_id: string) {
    const encodedQuery = encodeURIComponent(query);
    return this.http.get(
      environment.api_host +
        `sessions/${session_id}/documents/document-search/${encodedQuery}`,
    );
  }

  documentSearchautocomplete(query: string, session_id: string) {
    const encodedQuery = encodeURIComponent(query);
    return this.http.get(
      environment.api_host +
        `sessions/${session_id}/documents/document-search-suggestion/${encodedQuery}`,
    );
  }

  triggerOcrForSession(sessionId: string, connection_id?: string) {
    const body: any = {};
    if (
      connection_id &&
      connection_id !== 'undefined' &&
      connection_id !== ''
    ) {
      body.connectionId = connection_id;
    }
    return this.http.post<any>(
      environment.api_host + `sessions/${sessionId}/documents/trigger-ocr`,
      body,
    );
  }

  updateOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http.put<any>(
      environment.api_host +
        `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
      {
        value,
        output_type: outputType,
        org_id: orgId,
      },
    );
  }

  /**
   * Update QME report output; falls back to creating a new output if PUT is not available (e.g., 404 in some envs)
   */
  updateQmeReportOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http
      .put<any>(
        environment.api_host +
          `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
        { value, output_type: outputType, org_id: orgId },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            // Fallback: create a new output entry under the task
            return this.http.post<any>(
              environment.api_host +
                `sessions/${sessionId}/tasks/${taskId}/outputs`,
              {
                value,
                output_type: 'qme_report',
                org_id: orgId,
              },
            );
          }
          return throwError(() => err);
        }),
      );
  }

  /**
   * Update SIBTF report output; falls back to creating a new output if PUT is not available (e.g., 404 in some envs)
   */
  updateSibtfReportOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http
      .put<any>(
        environment.api_host +
          `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
        { value, output_type: outputType, org_id: orgId },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            return this.http.post<any>(
              environment.api_host +
                `sessions/${sessionId}/tasks/${taskId}/outputs`,
              {
                value,
                output_type: 'sibtf_report',
                org_id: orgId,
              },
            );
          }
          return throwError(() => err);
        }),
      );
  }

  /**
   * Update Medical-Legal report output; falls back to creating a new output if PUT is not available (e.g., 404 in some envs)
   */
  updateMedicalLegalReportOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http
      .put<any>(
        environment.api_host +
          `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
        { value, output_type: outputType, org_id: orgId },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            // Fallback: create a new output entry under the task
            return this.http.post<any>(
              environment.api_host +
                `sessions/${sessionId}/tasks/${taskId}/outputs`,
              {
                value,
                output_type: 'medical_legal_report',
                org_id: orgId,
              },
            );
          }
          return throwError(() => err);
        }),
      );
  }

  /**
   * Update PTP report output; falls back to creating a new output if PUT is not available (e.g., 404 in some envs)
   */
  updatePtpReportOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http
      .put<any>(
        environment.api_host +
          `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
        { value, output_type: outputType, org_id: orgId },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            // Fallback: create a new output entry under the task
            return this.http.post<any>(
              environment.api_host +
                `sessions/${sessionId}/tasks/${taskId}/outputs`,
              {
                value,
                output_type: 'ptp_report',
                org_id: orgId,
              },
            );
          }
          return throwError(() => err);
        }),
      );
  }

  updatePiReportOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http
      .put<any>(
        environment.api_host +
          `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
        { value, output_type: outputType, org_id: orgId },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            return this.http.post<any>(
              environment.api_host +
                `sessions/${sessionId}/tasks/${taskId}/outputs`,
              {
                value,
                output_type: 'pi_report',
                org_id: orgId,
              },
            );
          }
          return throwError(() => err);
        }),
      );
  }

  updateImpairmentRatingOutput(
    sessionId: string,
    outputId: string,
    taskId: string,
    value: any,
    outputType: string,
  ) {
    const orgId = this.getOrgId();
    return this.http
      .put<any>(
        environment.api_host +
          `sessions/${sessionId}/tasks/${taskId}/outputs/${outputId}`,
        { value, output_type: outputType, org_id: orgId },
      )
      .pipe(
        catchError((err) => {
          if (err?.status === 404) {
            return this.http.post<any>(
              environment.api_host +
                `sessions/${sessionId}/tasks/${taskId}/outputs`,
              {
                value,
                output_type: 'impairment_rating',
                org_id: orgId,
              },
            );
          }
          return throwError(() => err);
        }),
      );
  }

  regenerateQmeReport(
    sessionId: string,
    user_id: string,
    org_id: string,
    qme_report_type?: string,
    options?: RegenerateQmeReportOptions,
  ) {
    const typeQs =
      qme_report_type &&
      ['comprehensive', 'follow_up', 'supplemental'].includes(qme_report_type)
        ? `&qme_report_type=${encodeURIComponent(qme_report_type)}`
        : '';
    // The report-kind picker (SIBTF / Med Legal PTP templates) has been
    // retired — QME is the only kind now. Always sent explicitly because the
    // backend falls back to session.qmeReportKind from the DB when absent,
    // which would otherwise keep resolving to a stale legacy kind forever.
    const kindQs = '&report_kind=qme';
    const mmiForceDecisionQs = options?.mmiForceDecision
      ? `&force_impairment=${options.mmiForceDecision.forceImpairment}&force_decision_for_mmi_output_id=${encodeURIComponent(options.mmiForceDecision.forceDecisionForMmiOutputId)}`
      : '';
    // The initial request persists the package before MMI starts. A decision
    // follow-up deliberately omits it so the backend retains that selection.
    // aiScribeId (dictation-only packages) rides both.
    const body = {
      ...(options?.selectionId
        ? { selection: { id: options.selectionId } }
        : {}),
      ...(options?.aiScribeId ? { aiScribeId: options.aiScribeId } : {}),
    };
    return this.http.post<ReportTriggerResponse>(
      environment.api_host +
        `sessions/${sessionId}/regenerate-qme?user_id=${encodeURIComponent(user_id)}&org_id=${encodeURIComponent(org_id)}${typeQs}${kindQs}${mmiForceDecisionQs}`,
      body,
    );
  }

  regenerateSibtfReport(sessionId: string, user_id: string, org_id: string) {
    return this.http.post<any>(
      environment.api_host +
        `sessions/${sessionId}/regenerate-sibtf-report?user_id=${encodeURIComponent(user_id)}&org_id=${encodeURIComponent(org_id)}`,
      {},
    );
  }

  regeneratePiReport(
    sessionId: string,
    user_id: string,
    org_id: string,
    selectionId?: string,
  ) {
    const body = selectionId ? { selection: { id: selectionId } } : {};
    return this.http.post<any>(
      environment.api_host +
        `sessions/${sessionId}/regenerate-pi?user_id=${user_id}&org_id=${org_id}`,
      body,
    );
  }

  regenerateMedicalLegalReport(
    sessionId: string,
    user_id: string,
    org_id: string,
    medical_legal_report_type?: string,
  ) {
    const typeQs =
      medical_legal_report_type &&
      ['comprehensive', 'follow_up', 'supplemental'].includes(
        medical_legal_report_type,
      )
        ? `&medical_legal_report_type=${encodeURIComponent(medical_legal_report_type)}`
        : '';
    return this.http.post<ReportTriggerResponse>(
      environment.api_host +
        `sessions/${sessionId}/regenerate-medical-legal?user_id=${encodeURIComponent(user_id)}&org_id=${encodeURIComponent(org_id)}${typeQs}`,
      {},
    );
  }

  regeneratePtpReport(
    sessionId: string,
    user_id: string,
    org_id: string,
    ptp_report_type?: string,
    selectionId?: string,
    // Dictation-only package: the ONE AI Scribe the report is built from.
    aiScribeId?: string,
  ) {
    const typeQs =
      ptp_report_type &&
      ['comprehensive', 'follow_up', 'supplemental'].includes(ptp_report_type)
        ? `&ptp_report_type=${encodeURIComponent(ptp_report_type)}`
        : '';
    const body = {
      ...(selectionId ? { selection: { id: selectionId } } : {}),
      ...(aiScribeId ? { aiScribeId } : {}),
    };
    return this.http.post<any>(
      environment.api_host +
        `sessions/${sessionId}/regenerate-ptp-report?user_id=${encodeURIComponent(user_id)}&org_id=${encodeURIComponent(org_id)}${typeQs}`,
      body,
    );
  }

  checkPiReportExists(sessionId: string) {
    return this.http.get<{ exists: boolean }>(
      environment.api_host + `sessions/${sessionId}/check-pi-report`,
    );
  }

  generateImpairmentRating(
    sessionId: string,
    user_id: string,
    org_id: string,
    // Only sent when answering the force-decision prompt for a specific
    // strict-no MMI result. An unrelated plain generate/regenerate request
    // must omit this entirely — the backend distinguishes "no answer yet"
    // from "answered no", so an absent param must stay absent.
    mmiForceDecision?: MmiForceDecisionParams,
  ): Observable<GenerateImpairmentRatingResponse> {
    const mmiForceDecisionQs = mmiForceDecision
      ? `&force_impairment=${mmiForceDecision.forceImpairment}&force_decision_for_mmi_output_id=${encodeURIComponent(mmiForceDecision.forceDecisionForMmiOutputId)}`
      : '';
    return this.http.post<GenerateImpairmentRatingResponse>(
      environment.api_host +
        `sessions/${sessionId}/generate-impairment-rating?user_id=${user_id}&org_id=${org_id}${mmiForceDecisionQs}`,
      {},
    );
  }

  checkImpairmentRatingExists(sessionId: string) {
    return this.http.get<{ exists: boolean }>(
      environment.api_host + `sessions/${sessionId}/check-impairment-rating`,
    );
  }

  /**
   * `selectionId` is the Records-tab template pick (a GenerationProfileTarget
   * id, use case review_of_records). The backend saves it before starting the
   * run so the workflow is frozen onto that template; omitted keeps any prior
   * pick, or the default template runs.
   */
  generateReviewOfRecords(
    sessionId: string,
    user_id: string,
    org_id: string,
    selectionId?: string,
  ) {
    const body = selectionId ? { selection: { id: selectionId } } : {};
    return this.http.post<ReportTriggerResponse>(
      environment.api_host +
        `sessions/${sessionId}/generate-review-of-records?user_id=${encodeURIComponent(user_id)}&org_id=${encodeURIComponent(org_id)}`,
      body,
    );
  }

  /**
   * Manually trigger Summary generation (mirrors generateReviewOfRecords).
   * The summary is no longer auto-generated after an upload — the worker only
   * runs it when this endpoint publishes `generate_summary: true`.
   */
  generateSummary(sessionId: string, user_id: string, org_id: string) {
    return this.http.post<ReportTriggerResponse>(
      environment.api_host +
        `sessions/${sessionId}/generate-summary?user_id=${encodeURIComponent(user_id)}&org_id=${encodeURIComponent(org_id)}`,
      {},
    );
  }

  checkReviewOfRecordsExists(sessionId: string) {
    return this.http.get<{ exists: boolean }>(
      environment.api_host + `sessions/${sessionId}/check-review-of-records`,
    );
  }

  getAiScribeContents(sessionId: string) {
    return this.http.get<any[]>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/contents`,
    );
  }

  checkQmeReportExists(sessionId: string) {
    return this.http.get<{ exists: boolean }>(
      environment.api_host + `sessions/${sessionId}/check-qme-report`,
    );
  }

  getMmiGateState(sessionId: string) {
    return this.http.get<MmiGateStateResponse>(
      environment.api_host + `sessions/${sessionId}/mmi-gate-state`,
    );
  }

  getSessionUserNote(sessionId: string) {
    return this.http.get<SessionUserNote | null>(
      environment.api_host + `sessions/${sessionId}/note`,
    );
  }

  saveSessionUserNote(sessionId: string, content: string) {
    return this.http.post<SessionUserNote>(
      environment.api_host + `sessions/${sessionId}/note`,
      { content },
    );
  }

  initiateBatchUpload(sessionId: string, connection_id?: string) {
    const url =
      environment.api_host +
      `sessions/${sessionId}/documents/initiate-batch-upload` +
      (connection_id && connection_id !== 'undefined' && connection_id !== ''
        ? `?connection_id=${encodeURIComponent(connection_id)}`
        : '');
    return this.http.post<{ batchId: string }>(url, {});
  }

  finalizeBatch(sessionId: string, batchId: string) {
    return this.http.post<{
      success: boolean;
      batchId: string;
      message: string;
      alreadyFinalized?: boolean;
    }>(
      environment.api_host +
        `sessions/${sessionId}/documents/${batchId}/finalize-batch`,
      {},
    );
  }
  sendReportReadyNotification(
    sessionId: string,
    reportType: string,
    recipientEmail: string,
    sessionName: string,
  ) {
    return this.http.post<{ success: boolean }>(
      environment.api_host + `sessions/${sessionId}/notify-report-ready`,
      {
        report_type: reportType,
        recipient_email: recipientEmail,
        session_name: sessionName,
      },
    );
  }

  getSpecialties() {
    return this.http.get<Array<{ code: string; label: string; icon?: string }>>(
      environment.api_host + 'specialties',
    );
  }

  /**
   * Returns the DOCX template packages available for a use case in this org,
   * plus the owner's currently-saved pick (`currentSelection`).
   * Calls GET /api/v1/generation-templates?useCase=...&ownerType=...&ownerId=...&specialty=...
   *
   * `specialty` narrows the catalog to packages whose target template is either
   * specialty-agnostic or matches that specialty
   *
   * `generationMode` narrows it to AI-generated or dictation-only packages. Only
   * the PTP AI Scribe modal sends it; omitted means no filter (QME).
   */
  getGenerationTemplates(
    useCase: string,
    ownerType?: 'session' | 'ai_scribe',
    ownerId?: string,
    specialty?: string,
    generationMode?: GenerationMode,
    variant?: ReportVariant,
  ) {
    const params = new URLSearchParams({ useCase });
    if (ownerType) params.set('ownerType', ownerType);
    if (ownerId) params.set('ownerId', ownerId);
    if (specialty) params.set('specialty', specialty);
    if (generationMode) params.set('generationMode', generationMode);
    if (variant) params.set('variant', variant);
    return this.http.get<{
      useCase: string;
      templates: Array<{
        id: string;
        displayName: string;
        description: string | null;
        scope: 'provider' | 'organization' | 'default';
        profileSetKey?: string | null;
        // The package's variant; null for a set without one.
        variant?: ReportVariant | null;
      }>;
      // generationMode: the saved package's profile-set mode
      // ('ai_generated' | 'dictation'); null/absent on older API builds.
      currentSelection: { id: string; generationMode?: string | null } | null;
      temporalStartEnabled?: boolean;
      // This org creates reports of this kind through the Reports module.
      reportsEnabled?: boolean;
    }>(environment.api_host + `generation-templates?${params.toString()}`);
  }

  /**
   * Saves the owner's chosen GenerationProfileTarget before generation starts.
   * Calls PUT /api/v1/generation-templates/selection
   */
  saveGenerationSelection(
    ownerType: 'session' | 'ai_scribe',
    ownerId: string,
    useCase: string,
    generationProfileTargetId: string,
  ) {
    return this.http.put<void>(
      environment.api_host + 'generation-templates/selection',
      { ownerType, ownerId, useCase, generationProfileTargetId },
    );
  }

  /**
   * Hydrates an Output by fetching from S3 if needed.
   * Returns the same object with .value populated.
   */

  async hydrateOutputs(
    outputs: (Output | null | undefined)[],
    sessionId: string,
  ): Promise<(HydratedOutput | null)[]> {
    return Promise.all(outputs.map((o) => this.hydrateOutput(o, sessionId)));
  }

  async hydrateOutput(
    output: Output | null | undefined,
    sessionId: string,
  ): Promise<HydratedOutput | null> {
    if (!output) return null;

    const hasS3 = output.s3_key && output.s3_bucket && output.s3_url;

    try {
      if (hasS3) {
        const response = await firstValueFrom(
          this.getSignedUrl(output.s3_url!, sessionId),
        );

        if (!response?.signed_url) {
          console.error('[hydrateOutput] Missing signed_url');
          return { ...output, value: {} };
        }

        const content = await firstValueFrom(
          this.http.get<Record<string, any>>(response.signed_url, {
            responseType: 'json' as any,
          }),
        );

        return {
          ...output,
          value: content ?? {},
        };
      }

      return {
        ...output,
        value: output.value ?? {},
      };
    } catch (error) {
      console.error(
        `Failed to hydrate output ${output.id} (${output.output_type})`,
        error,
      );

      return {
        ...output,
        value: {},
      };
    }
  }
}
